// src/notifications/NotificationCenter.js
// Composant centralisé pour afficher les notifications - NE PAS MODIFIER

import React, { useState, useEffect } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import {
  NOTIFICATION_TYPES,
  NOTIFICATION_COLORS,
  NOTIFICATION_ICONS,
  NOTIFICATION_QUEUE,
} from './notificationTypes';

/**
 * Notification individuelle
 */
const Notification = ({ notification, onClose }) => {
  const { id, type, title, message, action } = notification;
  const color = NOTIFICATION_COLORS[type] || NOTIFICATION_COLORS.info;
  const icon = NOTIFICATION_ICONS[type] || 'ℹ';

  const styles = {
    container: {
      display: 'flex',
      gap: '12px',
      padding: '16px',
      marginBottom: '12px',
      backgroundColor: '#162033',
      border: `2px solid ${color}`,
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
      animation: 'slideIn 300ms ease-out',
      maxWidth: '400px',
      minWidth: '300px',
    },
    icon: {
      fontSize: '24px',
      color: color,
      fontWeight: 'bold',
      minWidth: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    content: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    title: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#FFFFFF',
    },
    message: {
      fontSize: '13px',
      color: '#A0AEC0',
      lineHeight: '1.4',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: '#A0AEC0',
      cursor: 'pointer',
      fontSize: '20px',
      padding: '0',
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'color 200ms',
    },
    actionButton: {
      marginTop: '8px',
      padding: '8px 12px',
      backgroundColor: color,
      color: '#0A0E1A',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '600',
      transition: 'opacity 200ms',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.icon}>{icon}</div>
      <div style={styles.content}>
        <div style={styles.title}>{title}</div>
        <div style={styles.message}>{message}</div>
        {action && (
          <button
            style={styles.actionButton}
            onClick={() => {
              if (action.callback) action.callback();
              onClose();
            }}
          >
            {action.label}
          </button>
        )}
      </div>
      <button
        style={styles.closeButton}
        onClick={onClose}
        onMouseEnter={(e) => (e.target.style.color = '#FFFFFF')}
        onMouseLeave={(e) => (e.target.style.color = '#A0AEC0')}
      >
        ✕
      </button>
    </div>
  );
};

/**
 * Conteneur centralisé pour toutes les notifications
 */
export const NotificationCenter = () => {
  const { notifications, removeNotification } = useNotifications();
  const [visibleNotifications, setVisibleNotifications] = useState([]);

  // Limiter le nombre de notifications visibles
  useEffect(() => {
    const limit = NOTIFICATION_QUEUE.maxVisible || 3;
    setVisibleNotifications(notifications.slice(-limit));
  }, [notifications]);

  const positionMap = {
    'top-left': { top: '20px', left: '20px' },
    'top-right': { top: '20px', right: '20px' },
    'bottom-left': { bottom: '20px', left: '20px' },
    'bottom-right': { bottom: '20px', right: '20px' },
  };

  const position = positionMap[NOTIFICATION_QUEUE.position] || positionMap['top-right'];

  const containerStyles = {
    position: 'fixed',
    ...position,
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxWidth: '440px',
    pointerEvents: 'auto',
  };

  return (
    <div style={containerStyles}>
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(${NOTIFICATION_QUEUE.position.includes('left') ? '-' : ''}400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOut {
          to {
            transform: translateX(${NOTIFICATION_QUEUE.position.includes('left') ? '-' : ''}400px);
            opacity: 0;
          }
        }
      `}</style>
      {visibleNotifications.map((notification) => (
        <Notification
          key={notification.id}
          notification={notification}
          onClose={() => removeNotification(notification.id)}
        />
      ))}
    </div>
  );
};

export default NotificationCenter;